// PaymentScreen.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, ShieldCheck, Loader2 } from 'lucide-react';
import '../styles/PaymentScreen.css';
import { createOutlineKey, createVlessKey, createObhodKey, createPayment, createPlategaPay } from '../api'; 

function PaymentScreen({
    selectedVPN,
    selectedMonths,
    prices,
    promoCode,
    appliedPromo,
}) {
  const navigate = useNavigate();
  const basePrice = prices[selectedVPN.id][selectedMonths];
  const finalPrice = appliedPromo ? Math.round(basePrice * 0.8) : basePrice;

  const [telegramId, setTelegramId] = useState(null);
  const [username, setUsername] = useState('');
  const [isIOS, setIsIOS] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Для индикации загрузки

  useEffect(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
      setIsIOS(window.Telegram.WebApp.platform === 'ios');
      
      const user = window.Telegram.WebApp.initDataUnsafe?.user;
      if (user) {
        setTelegramId(user.id);
        setUsername(user.username || user.first_name || 'User');
      }
    }

    if (process.env.NODE_ENV === 'development') {
      const params = new URLSearchParams(window.location.search);
      const tgIdFromUrl = params.get('tg_id');
      setTelegramId(tgIdFromUrl || 328036431);
      setUsername('TestUser');
    }
  }, []);

  const handlePay = async () => {
    if (!telegramId) {
      alert('Не удалось получить ваш Telegram ID');
      return;
    }

    setIsLoading(true);

    try {
      const serviceMap = {
        outline: 'outline',
        v2ray: 'vless',
        v2raybypass: 'obhod'
      };
      const service = serviceMap[selectedVPN.id];

      if (!service) {
        throw new Error('Неизвестный сервис');
      }

      const result = await createPayment(telegramId, username, finalPrice, service);

      if (result.payment_url) {
        // Если мы внутри Telegram WebApp
        if (window.Telegram?.WebApp) {
          // Открываем ссылку на оплату
          window.Telegram.WebApp.openLink(result.payment_url);

          // Через 5 секунд закрываем мини-приложение
          setTimeout(() => {
            window.Telegram.WebApp.close();
          }, 5000);
        } else {
          // Если открыто в обычном браузере (для разработки)
          window.location.href = result.payment_url;
        }
      }
    } catch (error) {
      console.error('Ошибка при создании платежа:', error);
      alert(error.message || 'Произошла ошибка при оплате. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlategaPay = async () => {
    if (!telegramId) {
      alert('Не удалось получить ваш Telegram ID');
      return;
    }

    setIsLoading(true);

    try {
      const serviceMap = {
        outline: 'outline',
        v2ray: 'vless',
        v2raybypass: 'obhod'
      };
      const service = serviceMap[selectedVPN.id];

      if (!service) {
        throw new Error('Неизвестный сервис');
      }

      const result = await createPlategaPay(telegramId, username, finalPrice, service);

      if (result.redirect_url) {
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.openLink(result.redirect_url);

          setTimeout(() => {
            window.Telegram.WebApp.close();
          }, 5000);
        } else {
          window.location.href = result.redirect_url;
        }
      }
    } catch (error) {
      console.error('Ошибка при создании платежа:', error);
      alert(error.message || 'Произошла ошибка при оплате. Попробуйте позже.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pay-wrapper">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="pay-content"
      >
        {/* Кнопка назад */}
        <button className="back-button" onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>

        {/* Заголовок */}
        <div className="pay-header">
          <div className="secure-badge">
            <ShieldCheck size={20} />
            <span>Безопасная оплата</span>
          </div>
          <h1 className="pay-title">Подтверждение</h1>
          <p className="pay-subtitle">Последний шаг до активации</p>
        </div>

        {/* Карточка с деталями заказа */}
        <div className="order-card">
          <div className="order-item">
            <span className="label">Тариф</span>
            <span className="value">{selectedVPN.name}</span>
          </div>
          <div className="order-item">
            <span className="label">Период</span>
            <span className="value">
              {selectedMonths} {selectedMonths === '1' ? 'месяц' : 'месяца'}
            </span>
          </div>

          {appliedPromo && (
            <div className="order-item promo-item">
              <span className="label">Промокод</span>
              <span className="value promo-code">{promoCode}</span>
            </div>
          )}

          <div className="price-breakdown">
            {appliedPromo && (
              <div className="price-row">
                <span>Старая цена</span>
                <span className="old-price">{basePrice} ₽</span>
              </div>
            )}
            <div className="price-row total">
              <span>К оплате</span>
              <span className="final-price">{finalPrice} ₽</span>
            </div>
          </div>
        </div>

        {/* Кнопка оплаты */}
        <motion.button
          className="pay-button"
          whileTap={{ scale: isLoading ? 1 : 0.95 }}
          onClick={handlePay}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              <span>Создание платежа...</span>
            </>
          ) : (
            <>
              <img
                src="/icons/yookassa.svg"
                alt="ЮKassa"
                className="payment-logo"
              />
              <span> Оплатить {finalPrice} ₽</span>
            </>
          )}
        </motion.button>

        <motion.button
          className="pay-button"
          whileTap={{ scale: isLoading ? 1 : 0.95 }}
          onClick={handlePlategaPay}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              <span>Создание платежа...</span>
            </>
          ) : (
            <>
              <img
                src="/icons/platega.svg"
                alt="Platega"
                className="payment-logo"
              />
              <span> Оплатить {finalPrice} ₽</span>
            </>
          )}
        </motion.button>
          
        <p className="security-note">
          Ваши данные защищены шифрованием. Мы не храним информацию о карте.
        </p>

        <div className="legal-links">
          <a
            href="https://telegra.ph/Politika-konfidencialnosti-08-21-102"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              if (window.Telegram?.WebApp) {
                window.Telegram.WebApp.openLink(
                  'https://telegra.ph/Politika-konfidencialnosti-08-21-102'
                );
              } else {
                window.open(
                  'https://telegra.ph/Politika-konfidencialnosti-08-21-102',
                  '_blank'
                );
              }
            }}
          >
            Политика конфиденциальности
          </a>
          <span className="separator">·</span>
          <a
            href="https://telegra.ph/Polzovatelskoe-soglashenie-08-21-71"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              if (window.Telegram?.WebApp) {
                window.Telegram.WebApp.openLink(
                  'https://telegra.ph/Polzovatelskoe-soglashenie-08-21-71'
                );
              } else {
                window.open(
                  'https://telegra.ph/Polzovatelskoe-soglashenie-08-21-71',
                  '_blank'
                );
              }
            }}
          >
            Пользовательское соглашение
          </a>
        </div>
      </motion.div>
    </div>
  );
}

export default PaymentScreen;